import React from "react";
import { Button, Card } from "antd";
import {
  WandSparkles,
  Crop,
  FileArchive,
  Eye,
  Layers,
  UploadCloud,
  ShieldCheck,
  Clock,
  IndianRupee,
  TrendingUp,
  Wallet,
  Rocket,
  Users,
  Headphones,
} from "lucide-react";
import WebsiteHeader from "../Components/WebsiteHeader";
import WebsiteFooter from "../Components/WebsiteFooter";

export default function Features() {

    const handleRedirect = (url) => {
        // Sends the user to the specified external website URL
        window.location.href = url;
    };
  return (
    <div className="bg-[#fbfaff] min-h-screen text-slate-900">
      <WebsiteHeader />

      <main className="max-w-[1200px] mx-auto px-4 py-12">
        <section className="text-center py-8">
          <div className="inline-flex bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-bold text-sm">
            ✨ Features
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold mt-5 leading-tight">
            Powerful Features to <br />
            Save More on{" "}
            <span className="text-purple-600">Meesho</span>
          </h1>

          <p className="text-slate-600 text-lg mt-5 max-w-2xl mx-auto">
            ShipLess AI is built to help Meesho sellers reduce shipping charges
            without compromising on image quality.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          <FeatureCard
            icon={<WandSparkles size={40} />}
            color="purple"
            title="AI-Powered Optimization"
            desc="Our advanced AI finds the perfect balance between size and quality to get the lowest shipping charge."
          />

          <FeatureCard
            icon={<Crop size={40} />}
            color="green"
            title="Smart Resizing"
            desc="Automatically resizes images to the ideal dimensions required by Meesho without affecting clarity."
          />

          <FeatureCard
            icon={<FileArchive size={40} />}
            color="orange"
            title="Maximum Compression"
            desc="High compression with smart algorithms reduces file size significantly while keeping images sharp."
          />

          <FeatureCard
            icon={<Eye size={40} />}
            color="blue"
            title="High Quality Output"
            desc="Optimized images maintain excellent quality to keep your listings professional and customer-friendly."
          />

          <FeatureCard
            icon={<Layers size={40} />}
            color="orange"
            title="Bulk Optimization"
            desc="Optimize multiple product images at once and save time. Perfect for sellers with large catalogs."
          />

          <FeatureCard
            icon={<UploadCloud size={40} />}
            color="purple"
            title="Direct Upload to Meesho"
            desc="Upload optimized images directly to Meesho with one click. No extra steps, no hassle."
          />

          <FeatureCard
            icon={<ShieldCheck size={40} />}
            color="green"
            title="100% Safe & Secure"
            desc="Your images and data are fully secure with us. We never share your data with anyone."
          />

          <FeatureCard
            icon={<Clock size={40} />}
            color="pink"
            title="Use Anytime, Anywhere"
            desc="Access ShipLess AI from anywhere, anytime. Optimize images on your desktop or mobile."
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8 bg-white rounded-3xl shadow-sm border p-7 mt-10">
          <div>
            <h2 className="text-4xl font-extrabold leading-tight">
              See the Difference, <br />
              <span className="text-purple-600">Feel the Savings</span>
            </h2>

            <p className="text-slate-600 mt-5 text-lg">
              Our AI reduces image size up to{" "}
              <b className="text-purple-600">80%*</b> without losing quality.
            </p>

            <div className="flex flex-wrap gap-4 mt-6 text-sm font-bold">
              <span className="text-green-600">● Lower file size</span>
              <span className="text-green-600">● Lower package size</span>
              <span className="text-green-600">● Lower shipping charge</span>
            </div>

            <div className="mt-7 bg-purple-50 text-purple-700 px-5 py-4 rounded-xl font-extrabold">
              ⚡ Lower Shipping Charge = More Orders = More Sales
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_50px_1fr] gap-4 items-center">
            <SavingImageCard
              label="Before Optimization"
              imageSize="842 KB"
              shipping="₹120"
              type="before"
            />

            <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center mx-auto font-bold text-2xl">
              →
            </div>

            <SavingImageCard
              label="After Optimization"
              imageSize="312 KB"
              shipping="₹60"
              type="after"
            />
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-3xl font-extrabold text-center">
            Why These Features{" "}
            <span className="text-purple-600">Matter?</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-8">
            <MatterCard
              icon={<IndianRupee />}
              title="Lower Shipping Charges"
              desc="Smaller size means lower shipping charge on Meesho."
            />
            <MatterCard
              icon={<TrendingUp />}
              title="More Orders"
              desc="Competitive shipping helps you win more orders."
            />
            <MatterCard
              icon={<Wallet />}
              title="More Sales"
              desc="More orders lead to more sales and better profits."
            />
            <MatterCard
              icon={<Rocket />}
              title="Business Growth"
              desc="Save more, earn more and grow your business faster."
            />
          </div>
        </section>

        <section className="mt-10 bg-purple-50 rounded-2xl p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-purple-600 text-white flex items-center justify-center">
              <ShieldCheck size={44} />
            </div>

            <div>
              <h2 className="text-2xl font-extrabold m-0">
                Built by a Meesho Seller, for Meesho Sellers
              </h2>
              <p className="text-slate-600 m-0 mt-2">
                We understand your challenges and created ShipLess AI to help
                you save more and grow your business.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <SmallTrust icon={<Users />} label="Sellers First" />
            <SmallTrust icon={<ShieldCheck />} label="Trusted" />
            <SmallTrust icon={<TrendingUp />} label="Always Improving" />
            <SmallTrust icon={<Headphones />} label="Here to Help" />
          </div>
        </section>

        <section className="mt-8 bg-gradient-to-r from-purple-700 to-purple-600 rounded-2xl p-8 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="text-6xl">🎁</div>
            <div>
              <h2 className="text-3xl font-extrabold m-0">
                Start Saving on Shipping Charges Today!
              </h2>
              <p className="m-0 mt-2">
                Join the beta and get 30 free credits instantly.
              </p>
            </div>
          </div>

          <div>
            <Button onClick={() => handleRedirect('https://dashboard.shipless.in/')} size="large" className="h-14 px-10 font-bold">
                
              Get 30 Free Credits →
            </Button>
            <p className="text-sm text-purple-100 text-center mt-2">
              No credit card required
            </p>
          </div>
        </section>
      </main>

      <WebsiteFooter/>
    </div>
  );
}

function FeatureCard({ icon, title, desc, color }) {
  const map = {
    purple: "bg-purple-100 text-purple-600",
    green: "bg-green-100 text-green-600",
    orange: "bg-orange-100 text-orange-600",
    blue: "bg-blue-100 text-blue-600",
    pink: "bg-pink-100 text-pink-600",
  };

  return (
    <Card className="rounded-2xl shadow-sm text-center h-full">
      <div
        className={`w-20 h-20 rounded-2xl mx-auto flex items-center justify-center ${map[color]}`}
      >
        {icon}
      </div>

      <h3 className="text-lg font-extrabold mt-6">{title}</h3>
      <p className="text-slate-600 leading-6">{desc}</p>
    </Card>
  );
}

function SavingImageCard({ label, imageSize, shipping, type }) {
  const isAfter = type === "after";

  return (
    <div
      className={`rounded-2xl p-4 border ${
        isAfter ? "bg-green-50 border-green-200" : "bg-purple-50"
      }`}
    >
      <div className="bg-white rounded-xl px-3 py-2 inline-block text-xs font-bold mb-4">
        {label}
      </div>

      <div className="h-56 rounded-xl bg-white flex items-center justify-center text-7xl">
        👗
      </div>

      <div className="mt-4 bg-white rounded-xl p-4 grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-slate-500 m-0">Image Size</p>
          <h3
            className={`text-xl font-extrabold m-0 ${
              isAfter ? "text-green-600" : "text-red-500"
            }`}
          >
            {imageSize}
          </h3>
        </div>

        <div>
          <p className="text-xs text-slate-500 m-0">Estimated Shipping</p>
          <h3
            className={`text-xl font-extrabold m-0 ${
              isAfter ? "text-green-600" : "text-red-500"
            }`}
          >
            {shipping}
          </h3>
        </div>
      </div>
    </div>
  );
}

function MatterCard({ icon, title, desc }) {
  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
          {icon}
        </div>

        <div>
          <h3 className="font-extrabold m-0">{title}</h3>
          <p className="text-slate-500 m-0 mt-1 text-sm">{desc}</p>
        </div>
      </div>
    </Card>
  );
}

function SmallTrust({ icon, label }) {
  return (
    <div>
      <div className="w-12 h-12 rounded-xl bg-white text-purple-600 flex items-center justify-center mx-auto">
        {icon}
      </div>
      <p className="font-semibold m-0 mt-2">{label}</p>
    </div>
  );
}